// Networking Roulette admin API client
import api from "./axios";

const BASE = "/events/roulette";

function normalizeGameId(value) {
  if (!value) return "";
  if (typeof value === "object") {
    return String(
      value.id ||
      value.game_id ||
      value.gameId ||
      value.slug ||
      value.value ||
      ""
    ).trim();
  }
  const normalized = String(value).trim();
  return normalized === "[object Object]" ? "" : normalized;
}

export async function fetchParticipants(eventId) {
  const res = await api.get(`${BASE}/event/${eventId}/participants/`);
  return res.data;
}

export async function setNewJoinersTableHost(eventId, tableNumber, userId) {
  const res = await api.post(`${BASE}/event/${eventId}/table-host/`, {
    table_number: tableNumber,
    user_id: userId || null,
  });
  return res.data;
}

export async function resetSandboxNewJoinersSeating(eventId, tableCount) {
  const res = await api.post(`${BASE}/event/${eventId}/sandbox-seating-reset/`, {
    table_count: tableCount,
  });
  return res.data;
}

export async function fetchPartyAdminEvents() {
  const res = await api.get(`${BASE}/events/`);
  return res.data;
}

export async function refreshAttendees(eventId) {
  const res = await api.get(`${BASE}/event/${eventId}/refresh/`);
  return res.data;
}

export async function resolveTableHelpRequest(eventId, requestId) {
  const res = await api.post(`${BASE}/event/${eventId}/table-help/resolve/`, {
    request_id: requestId,
  });
  return res.data;
}

export async function setParticipantOverride(eventId, payload) {
  const res = await api.post(`${BASE}/event/${eventId}/participant-override/`, payload);
  return res.data;
}

export async function resetParticipantOverrides(eventId) {
  const res = await api.post(`${BASE}/event/${eventId}/participant-overrides/reset/`);
  return res.data;
}

export async function fetchBuddyGroups(eventId) {
  const res = await api.get(`${BASE}/event/${eventId}/buddy-groups/`);
  return res.data;
}

export async function moveBuddyGroupMember(eventId, payload) {
  const res = await api.post(`${BASE}/event/${eventId}/buddy-groups/move/`, payload);
  return res.data;
}

export async function generateSeating({
  eventId,
  userIds,
  nTables,
  seatsConfig,
  autoSeats = false,
  courtColumns = null,
  courtRows = null,
  allowRepeats = false,
}) {
  const res = await api.post(`${BASE}/generate/`, {
    event_id: eventId,
    user_ids: userIds,
    n_tables: nTables,
    auto_seats: Boolean(autoSeats),
    seats_config: seatsConfig,
    court_columns: courtColumns,
    court_rows: courtRows,
    allow_repeats: Boolean(allowRepeats),
  });
  return res.data;
}

export async function persistRound({ sessionId, layout }) {
  const res = await api.post(`${BASE}/round/`, {
    session_id: sessionId,
    layout,
  });
  return res.data;
}

export async function notifySeating({ eventId, layout, roundNumber, sessionId, restingUserIds = [], allowRepeats = false }) {
  const res = await api.post(`${BASE}/notify/`, {
    event_id: eventId,
    session_id: sessionId,
    layout,
    round_number: roundNumber,
    resting_user_ids: restingUserIds,
    allow_repeats: Boolean(allowRepeats),
  });
  return res.data;
}

export async function assignLateParticipant({ eventId, userId, sessionId, roundNumber, targetTableNumber = null, restartTableGame = false }) {
  const res = await api.post(`${BASE}/event/${eventId}/assign-late/`, {
    user_id: userId,
    expected_session_id: sessionId,
    expected_round_number: roundNumber,
    ...(targetTableNumber == null ? {} : { target_table_number: targetTableNumber }),
    ...(restartTableGame ? { restart_table_game: true } : {}),
  });
  return res.data;
}

export async function startRound({ eventId, sessionId, layout, gameId, roundNumber, allowRepeats = false, portraitMode, aliasMode }) {
  const res = await api.post(`${BASE}/start-round/`, {
    event_id: eventId,
    session_id: sessionId,
    layout,
    game_id: normalizeGameId(gameId),
    round_number: roundNumber,
    allow_repeats: Boolean(allowRepeats),
    ...(portraitMode ? { portrait_mode: portraitMode } : {}),
    ...(aliasMode ? { alias_mode: aliasMode } : {}),
  });
  return res.data;
}

export async function startSportRotation(eventId, settings) {
  const res = await api.post(`${BASE}/event/${eventId}/sport-rotation/start/`, settings);
  return res.data;
}

export async function fetchSportRotation(eventId) {
  const res = await api.get(`${BASE}/event/${eventId}/sport-rotation/`);
  return res.data;
}

export async function controlSportRotation(eventId, action, extraPayload = {}) {
  const res = await api.post(`${BASE}/event/${eventId}/sport-rotation/`, {
    action,
    ...extraPayload,
  });
  return res.data;
}

export async function triggerGameAction({ eventId, sessionId, layout, gameId, roundNumber, portraitMode, aliasMode }) {
  const res = await api.post(`${BASE}/game-action/`, {
    event_id: eventId,
    session_id: sessionId,
    layout,
    game_id: normalizeGameId(gameId),
    round_number: roundNumber,
    ...(portraitMode ? { portrait_mode: portraitMode } : {}),
    ...(aliasMode ? { alias_mode: aliasMode } : {}),
  });
  return res.data;
}

export async function triggerHostGameAction(eventId, action, extraPayload = {}) {
  const res = await api.post(`${BASE}/event/${eventId}/host-game-action/`, {
    action,
    ...extraPayload,
  });
  return res.data;
}

export async function fetchPartySessionState(eventId) {
  const res = await api.get(`${BASE}/session/${eventId}/me/`);
  return res.data;
}

export async function fetchTableInspector(eventId, tableNumber) {
  const res = await api.get(`${BASE}/event/${eventId}/table/${tableNumber}/`);
  return res.data;
}

export async function triggerTableAction(eventId, tableNumber, action, extraPayload = {}) {
  const res = await api.post(`${BASE}/event/${eventId}/table/${tableNumber}/action/`, {
    action,
    ...extraPayload,
    ...(extraPayload?.game_id ? { game_id: normalizeGameId(extraPayload.game_id) } : {}),
  });
  return res.data;
}

export async function confirmPartyTableArrival(eventId) {
  const res = await api.post(`${BASE}/session/${eventId}/arrived/`);
  return res.data;
}

export async function requestPartyTableHelp(eventId) {
  const res = await api.post(`${BASE}/session/${eventId}/help/`);
  return res.data;
}

export async function savePartyDisplayName(eventId, displayName) {
  const res = await api.post(`${BASE}/session/${eventId}/set-name/`, {
    display_name: displayName,
  });
  return res.data;
}

export async function saveSportSelfAssessment(eventId, level) {
  const res = await api.post(`${BASE}/session/${eventId}/skill-level/`, {
    level,
  });
  return res.data;
}

export async function fetchSportSelfAssessment(eventId) {
  const res = await api.get(`${BASE}/session/${eventId}/skill-level/`);
  return res.data;
}

export async function setSportParticipantLevel(eventId, userId, level) {
  const numericLevel = Number(level);
  const clearLevel = level == null;
  const res = await api.post(`${BASE}/event/${eventId}/participant-skill-level/`, {
    user_id: userId,
    action: clearLevel ? "clear" : "set",
    ...(clearLevel ? {} : { level: numericLevel }),
  });
  return res.data;
}

export async function updatePartySessionStatus(eventId, status) {
  const res = await api.post(`${BASE}/session/${eventId}/status/`, { status });
  return res.data;
}

export async function submitFreePlayAction(eventId, action, gameId = "") {
  const res = await api.post(`${BASE}/session/${eventId}/free-play/action/`, {
    action,
    ...(gameId ? { game_id: normalizeGameId(gameId) } : {}),
  });
  return res.data;
}

export async function finishFreePlayRound(eventId, sessionId, roundNumber) {
  const res = await api.post(`${BASE}/session/${eventId}/free-play/finish/`, {
    session_id: sessionId,
    round_number: roundNumber,
  });
  return res.data;
}

export async function finishNetworkingRound(eventId, sessionId, roundNumber, allowRepeats = false) {
  const res = await api.post(`${BASE}/session/${eventId}/networking/finish/`, {
    session_id: sessionId,
    round_number: roundNumber,
    allow_repeats: Boolean(allowRepeats),
  });
  return res.data;
}

export async function submitFactsDuelAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/facts-duel/action/`, { action, ...payload });
  return res.data;
}

export async function fetchFactsDuelState(eventId) {
  const res = await api.get(`${BASE}/session/${eventId}/facts-duel/state/`);
  return res.data?.facts_duel || null;
}

export async function submitDontSayWordAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/dont-say-word/action/`, { action, ...payload });
  return res.data;
}

export async function submitNeverHaveIEverAction(eventId, action) {
  const res = await api.post(`${BASE}/session/${eventId}/never-have-i-ever/action/`, { action });
  return res.data;
}

export async function submitPredictAnswerAction(eventId, action) {
  const res = await api.post(`${BASE}/session/${eventId}/predict-answer/action/`, { action });
  return res.data;
}

export async function submitProfessionAliasAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/profession-alias/action/`, { action, ...payload });
  return res.data;
}

export async function submitTeamAliasAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/team-alias/action/`, { action, ...payload });
  return res.data;
}

export async function submitDrawingAliasAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/drawing-alias/action/`, { action, ...payload });
  return res.data;
}

export async function submitPromptsQnaAction(eventId, action, notificationId) {
  const endpoint = action === "ready"
    ? "/events/api/party-games/prompts-qna/ready/"
    : "/events/api/party-games/prompts-qna/guessed/";
  const res = await api.post(endpoint, { event_id: eventId, notification_id: notificationId });
  return res.data;
}

export async function submitConversationCardsAction(_eventId, action, notificationId) {
  const endpoint = action === "ready"
    ? "/events/api/party-games/conversation-cards/ready/"
    : "/events/api/party-games/conversation-cards/next/";
  const res = await api.post(endpoint, { notification_id: notificationId });
  return res.data;
}

export async function submitConnectionRelayAction(eventId, action) {
  const res = await api.post(`${BASE}/session/${eventId}/connection-relay/action/`, { action });
  return res.data;
}

export async function submitQuizLobbyAction(eventId, action = "ready") {
  const res = await api.post(`${BASE}/session/${eventId}/quiz-lobby/action/`, { action });
  return res.data;
}

export async function submitTableActivityAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/table-activity/action/`, {
    action,
    ...payload,
  });
  return res.data;
}

export async function submitNetworkingBingoAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/networking-bingo/action/`, { action, ...payload });
  return res.data;
}

export async function submitHotPotatoAction(eventId, action) {
  const res = await api.post(`${BASE}/session/${eventId}/hot-potato/action/`, { action });
  return res.data;
}

export async function submitPortraitGalleryAction(eventId, action, payload = {}) {
  if (payload?.drawing instanceof Blob) {
    const form = new FormData();
    form.append("action", action);
    form.append("drawing", payload.drawing, "portrait.png");
    const res = await api.post(`${BASE}/session/${eventId}/portrait-gallery/action/`, form);
    return res.data;
  }
  const res = await api.post(`${BASE}/session/${eventId}/portrait-gallery/action/`, {
    action,
    ...payload,
  });
  return res.data;
}

export async function submitSpyAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/spy/action/`, { action, ...payload });
  return res.data;
}

export async function submitAlchemyAction(eventId, action, payload = {}) {
  const res = await api.post(`${BASE}/session/${eventId}/alchemy/action/`, { action, ...payload });
  return res.data;
}

export async function submitPresentationAnswer(eventId, optionId, answerText = "") {
  const res = await api.post(`${BASE}/session/${eventId}/presentation-answer/`, {
    option_id: optionId,
    answer_text: answerText,
  });
  return res.data;
}

export async function submitPresentationQuestion(eventId, questionText) {
  const res = await api.post(`${BASE}/session/${eventId}/submit-question/`, {
    question_text: questionText,
  });
  return res.data;
}

export async function setPresentationMode(eventId, mode, payload = {}) {
  const res = await api.post(`${BASE}/event/${eventId}/presentation-mode/`, { mode, ...payload });
  return res.data;
}

export async function createPresentationQuestion(eventId, payload) {
  const res = await api.post(`${BASE}/event/${eventId}/presentation-question/`, payload);
  return res.data;
}

export async function sendPresentationQuestion(eventId, questionId, payload = {}) {
  const res = await api.post(`${BASE}/event/${eventId}/presentation-send/`, {
    question_id: questionId,
    ...payload,
  });
  return res.data;
}

export async function clearPresentationQuestion(eventId) {
  try {
    const res = await api.post(`${BASE}/event/${eventId}/presentation-clear/`);
    return res.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status && status !== 404 && status !== 405) {
      throw error;
    }
    const fallback = await api.post(`${BASE}/event/${eventId}/presentation-mode/`, {
      mode: "presentation",
      clear_active_question: true,
    });
    return fallback.data;
  }
}

export async function deletePresentationQuestion(eventId, questionId) {
  const res = await api.post(`${BASE}/event/${eventId}/presentation-delete/`, {
    question_id: questionId,
  });
  return res.data;
}
